import { graphql } from 'gatsby';
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text';
import React, { useEffect, useRef, useState } from 'react';

import { postContactUs } from '../../../helpers/api/contact-us-api-client';
import { isBrowser } from '../../../helpers/is-browser';
import { FormState, useForm } from '../../../hooks/use-form';
import { IMeetingPlaceLinkToAnyPage } from '../../../queries/fragments/meetingPlaceLinkToAnyPage';
import { RichText } from '../../rich-text/rich-text';
import Button from '../../ui/button/button';
import { Checkbox } from '../../ui/checkbox/checkbox';
import InputField from '../../ui/input-field/input-field';
import LinkWrapper from '../../ui/link-wrapper/link-wrapper';
import Spinner from '../../ui/spinner/spinner';

export interface ContactFormBlockProps {
  contactFormThankYouMessageText: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  contactFormThankYouMessageTitle: string;
  emailFieldErrorMessage?: string;
  emailFieldLabel: string;
  emailFieldPlaceholder: string;
  emailValidationFieldErrorMessage?: string;
  emailValidationFieldLabel: string;
  emailValidationFieldPlaceholder: string;
  id: string;
  messageFieldErrorMessage?: string;
  messageFieldLabel: string;
  nameFieldErrorMessage?: string;
  nameFieldLabel: string;
  nameFieldPlaceholder: string;
  phoneNumberFieldLabel: string;
  phoneNumberFieldPlaceholder: string;
  phoneNumberFieldErrorMessage: string;
  submitButtonLabel: string;
  termsAndConditionsCheckboxLabel: string;
  termsAndConditionsLinkLabel: string;
  termsAndConditionsPageLink: IMeetingPlaceLinkToAnyPage;
  integrationId: string;
  genericErrorTitle?: string;
  genericErrorMessage?: RenderRichTextData<ContentfulRichTextGatsbyReference>;
  genericErrorReloadButtonLabel?: string;
}
const ContactFormBlock = (formProps: ContactFormBlockProps) => {
  //Reload page
  const onClickReload = () => {
    if (isBrowser) {
      window.location.reload();
    }
  };

  //Spinner
  const [showSpinner, setShowSpinner] = useState<boolean>(false);

  //Set up form
  const initalFormState: FormState = {
    name: { value: '', required: true, error: false, maxLength: 100 },
    email: { value: '', required: true, error: false, maxLength: 254 },
    validateEmail: { value: '', required: true, error: false },
    phoneNumber: {
      value: '',
      required: false,
      error: false,
      maxLength: 30,
    },
    message: { value: '', required: true, error: false, maxLength: 4096 },
  };

  const {
    onChange,
    values,
    updateInputValidityOnBlurOrFocus,
    updateFormValidity,
  } = useForm(initalFormState);

  //Checkbox
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const onCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const form = event.currentTarget.form;
    updateFormValidity(form);
    setAgreeToTerms(!agreeToTerms);
  };

  // Submit button
  const [submitButtonDisabled, setSubmitButtonDisabled] =
    useState<boolean>(false);

  useEffect(() => {
    //Submit button disabled unless user agrees to terms or all fields are valid
    const disabled: boolean[] = [!agreeToTerms];
    for (const field of Object.values(values)) {
      disabled.push(field.error || (field.required && field.value === ''));
    }
    setSubmitButtonDisabled(disabled.includes(true));
  }, [setSubmitButtonDisabled, values, agreeToTerms]);

  //Submit form
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [showGenericError, setShowGenericError] = useState<boolean>(false);

  const messageRef = useRef<null | HTMLDivElement>(null);

  //On submit form or show error, scroll to top in order to show entire message.
  useEffect(() => {
    if (
      (formSubmitted || showGenericError) &&
      isBrowser &&
      messageRef &&
      messageRef.current
    ) {
      messageRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [formSubmitted, showGenericError, messageRef]);

  const onSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();

    if (!event.currentTarget.checkValidity()) {
      updateFormValidity(event.currentTarget);
      return;
    }

    setShowSpinner(true);

    try {
      await postContactUs(
        {
          name: values.name.value.trim(),
          confirmationEmail: values.validateEmail.value,
          email: values.email.value,
          message: values.message.value,
          phoneNumber: values.phoneNumber.value,
          termsOfUseConfirmation: agreeToTerms,
        },
        formProps.integrationId,
      );
      setFormSubmitted(true);
    } catch (e) {
      setShowGenericError(true);
    }

    setShowSpinner(false);
  };

  return (
    <>
      <div ref={messageRef}>
        {formSubmitted && (
          <>
            <h2 className="text-heading mb-4 font-title text-neutral-grey-700 md:text-headingxxl">
              {formProps.contactFormThankYouMessageTitle}
            </h2>
            {formProps?.contactFormThankYouMessageText && (
              <RichText richText={formProps.contactFormThankYouMessageText} />
            )}
          </>
        )}
        {showGenericError && (
          <>
            <h2 className="mb-5 font-title text-headingm text-neutral-grey-900 lg:mb-8 lg:text-headingxxl">
              {formProps.genericErrorTitle}
            </h2>

            {formProps.genericErrorMessage && (
              <RichText richText={formProps.genericErrorMessage} />
            )}
            <Button onClick={onClickReload} className="mt-8">
              {formProps.genericErrorReloadButtonLabel}
            </Button>
          </>
        )}
      </div>

      {!formSubmitted && !showGenericError && (
        <form noValidate onSubmit={onSubmit} className="grid grid-cols-1 gap-4">
          <InputField
            name="name"
            id="name"
            disabled={false}
            type="text"
            label={formProps.nameFieldLabel}
            placeholder={formProps.nameFieldPlaceholder}
            autoComplete={false}
            value={values.name.value}
            onChange={onChange}
            onBlur={updateInputValidityOnBlurOrFocus}
            required={values.name.required}
            hasError={values.name.error}
            errorMessage={formProps.nameFieldErrorMessage}
            maxLength={values.name.maxLength}
          />

          <InputField
            name="email"
            id="email"
            disabled={false}
            type="email"
            label={formProps.emailFieldLabel}
            placeholder={formProps.emailFieldPlaceholder}
            autoComplete={false}
            value={values.email.value}
            hasError={values.email.error}
            required={values.email.required}
            onChange={onChange}
            onBlur={updateInputValidityOnBlurOrFocus}
            maxLength={values.email.maxLength}
            errorMessage={formProps.emailFieldErrorMessage}
            pattern="^[a-zA-Z0-9._\-]+@[a-zA-Z0-9.\-]+[a-zA-Z]{2,}"
          />

          <InputField
            name="validateEmail"
            id="validateEmail"
            disabled={false}
            type="text"
            label={formProps.emailValidationFieldLabel}
            placeholder={formProps.emailValidationFieldPlaceholder}
            autoComplete={false}
            value={values.validateEmail.value}
            errorMessage={formProps.emailValidationFieldErrorMessage}
            hasError={values.validateEmail.error}
            required={values.validateEmail.required}
            onChange={onChange}
            onBlur={updateInputValidityOnBlurOrFocus}
            pattern={`^(${values.email.value})$`}
          />

          <InputField
            name="phoneNumber"
            id="phoneNumber"
            disabled={false}
            type="tel"
            label={formProps.phoneNumberFieldLabel}
            placeholder={formProps.phoneNumberFieldPlaceholder}
            autoComplete={false}
            value={values.phoneNumber.value}
            hasError={values.phoneNumber.error}
            errorMessage={formProps.phoneNumberFieldErrorMessage}
            required={values.phoneNumber.required}
            onChange={onChange}
            onBlur={updateInputValidityOnBlurOrFocus}
            maxLength={values.phoneNumber.maxLength}
            pattern="[+]?[\(\) 0-9]*"
          />

          <InputField
            name="message"
            id="message"
            disabled={false}
            label={formProps.messageFieldLabel}
            autoComplete={false}
            isTextarea={true}
            value={values.message.value}
            errorMessage={formProps.messageFieldErrorMessage}
            hasError={values.message.error}
            required={values.message.required}
            onChange={onChange}
            onBlur={updateInputValidityOnBlurOrFocus}
            pattern="<[^<>]+>"
            maxLength={values.message.maxLength}
          />

          <Checkbox
            id="termsOfUse"
            checked={agreeToTerms}
            name="termsOfUse"
            onChange={onCheckboxChange}
          >
            <span className="text-bodys">
              {formProps.termsAndConditionsCheckboxLabel}{' '}
              <LinkWrapper
                data={formProps.termsAndConditionsPageLink}
                className={'underline'}
                target="_blank"
                rel="noreferrer"
              >
                {formProps.termsAndConditionsLinkLabel}
              </LinkWrapper>
            </span>
          </Checkbox>

          <div className="mt-4">
            <Button
              size="regular"
              type="submit"
              variant="primary"
              disabled={submitButtonDisabled}
            >
              {formProps.submitButtonLabel}
            </Button>
          </div>
        </form>
      )}
      <Spinner show={showSpinner} />
    </>
  );
};

export default ContactFormBlock;

export const query = graphql`
  fragment ContactFormBlock on ContentfulBlockContactform {
    blocktype: __typename
    contactFormThankYouMessageText {
      raw
    }
    contactFormThankYouMessageTitle
    emailFieldErrorMessage
    emailFieldLabel
    emailFieldPlaceholder
    emailValidationFieldErrorMessage
    emailValidationFieldLabel
    emailValidationFieldPlaceholder
    id
    messageFieldErrorMessage
    messageFieldLabel
    nameFieldErrorMessage
    nameFieldLabel
    nameFieldPlaceholder
    phoneNumberFieldLabel
    phoneNumberFieldPlaceholder
    phoneNumberFieldErrorMessage
    submitButtonLabel
    termsAndConditionsCheckboxLabel
    termsAndConditionsLinkLabel
    termsAndConditionsPageLink {
      ...MeetingPlaceLinkToAnyPage
    }
    genericErrorTitle
    genericErrorMessage {
      raw
    }
    genericErrorReloadButtonLabel
  }
`;
